import { useState, useEffect } from 'react';
import { X, Sparkles, Crown } from 'lucide-react';
import whatsNewData from '@/data/whatsNew.json';

interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Announcement {
  id: string;
  title: string;
  date: string;
  category: 'NEW_FEATURE' | 'ENHANCEMENT' | 'NEW_PLATFORM';
  featured: boolean;
  premium: boolean;
  image?: string;
  badge?: 'NEW' | 'PREMIUM';
  description: string;
  highlights: string[];
}

interface Badge {
  label: string;
  color: string;
  textColor: string;
}

interface Category {
  label: string;
  color: string;
}

const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ isOpen, onClose }) => {
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  // Show first announcement by default
  useEffect(() => {
    if (isOpen && !selectedAnnouncement) {
      setSelectedAnnouncement(whatsNewData.announcements[0] as Announcement);
    }
  }, [isOpen, selectedAnnouncement]);

  if (!isOpen) return null;

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const getBadgeStyles = (badge: 'NEW' | 'PREMIUM'): React.CSSProperties => {
    const config = whatsNewData.badges[badge] as Badge;
    if (!config) return {};

    return {
      backgroundColor: config.color,
      color: config.textColor
    };
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--st-bg-surface)',
          border: '2px solid var(--st-border-light)',
          boxShadow: '0 20px 60px rgba(92, 64, 51, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-8 py-6 flex items-center justify-between border-b"
          style={{
            backgroundColor: 'var(--st-bg-surface)',
            borderColor: 'var(--st-border-light)'
          }}
        >
          <h2
            className="text-3xl font-bold flex items-center gap-3"
            style={{
              fontFamily: 'Chewy, cursive',
              color: 'var(--st-primary)'
            }}
          >
            <Sparkles className="w-7 h-7" />
            What's New?
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all hover:bg-orange-50 dark:hover:bg-orange-900/20"
            style={{ color: 'var(--st-text-heading)' }}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)] px-8 py-6" style={{ backgroundColor: 'var(--st-bg-page)' }}>
          <div className="space-y-8">
            {(whatsNewData.announcements as Announcement[]).map((announcement, index) => (
              <div
                key={announcement.id}
                className="rounded-xl p-6 border-b pb-8 last:border-b-0 transition-all hover:shadow-md"
                style={{
                  backgroundColor: 'var(--st-bg-surface)',
                  border: '1px solid var(--st-border-light)',
                  borderBottom: index === whatsNewData.announcements.length - 1 ? '1px solid var(--st-border-light)' : '1px solid var(--st-border-light)',
                  boxShadow: '0 2px 8px rgba(92, 64, 51, 0.08)'
                }}
              >
                {/* Announcement Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3
                        className="text-xl font-bold"
                        style={{
                          fontFamily: 'Chewy, cursive',
                          color: 'var(--st-text-heading)'
                        }}
                      >
                        {announcement.title}
                      </h3>

                      {/* Badges */}
                      {announcement.badge && (
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                          style={getBadgeStyles(announcement.badge)}
                        >
                          {(whatsNewData.badges[announcement.badge] as Badge).label}
                        </span>
                      )}

                      {announcement.premium && !announcement.badge && (
                        <Crown className="w-4 h-4" style={{ color: '#FFD700' }} />
                      )}
                    </div>

                    {/* Date */}
                    <p
                      className="text-sm"
                      style={{
                        fontFamily: 'Quicksand, sans-serif',
                        color: 'var(--st-text-muted)'
                      }}
                    >
                      {formatDate(announcement.date)}
                    </p>
                  </div>

                  {/* Category Badge */}
                  <span
                    className="px-3 py-1 rounded-lg text-xs font-semibold"
                    style={{
                      backgroundColor: 'var(--st-border-light)',
                      color: (whatsNewData.categories[announcement.category] as Category).color,
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    {(whatsNewData.categories[announcement.category] as Category).label}
                  </span>
                </div>

                {/* Description */}
                <p
                  className="text-base mb-4 leading-relaxed"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: 'var(--st-text-body)'
                  }}
                >
                  {announcement.description}
                </p>

                {/* Highlights */}
                {announcement.highlights && announcement.highlights.length > 0 && (
                  <ul className="space-y-2">
                    {announcement.highlights.map((highlight, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3"
                      >
                        <span
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: 'var(--st-primary)' }}
                        />
                        <span
                          className="text-sm leading-relaxed"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: 'var(--st-text-body)'
                          }}
                        >
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Featured announcement gets extra spacing */}
                {announcement.featured && index < whatsNewData.announcements.length - 1 && (
                  <div className="mt-6" />
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            className="mt-8 pt-6 border-t text-center"
            style={{ borderColor: 'var(--st-border-light)' }}
          >
            <p
              className="text-sm"
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: 'var(--st-text-muted)'
              }}
            >
              Want to experience these features yourself?
            </p>
            <p
              className="text-sm mt-1"
              style={{
                fontFamily: 'Quicksand, sans-serif',
                color: 'var(--st-primary)',
                fontWeight: 600
              }}
            >
              Start creating your first story today ✨
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
